'use client'
import { useTranslations } from "next-intl"
import styles from "./Plan.module.css"
import { Membership, Price } from "@/interfaces/memberships_types"
import Icon_Check from "@/assets/img/Icon_Check.svg"
import Icon_Info from "@/assets/img/Icon_Info.svg"
import Icon from "@/components/UI/Icon/Icon"
import getSymbolFromCurrency from 'currency-symbol-map'
import { useMemo, useState } from "react"
import { useWindowSize } from "@/hooks/useWindowsSize"
import { motion } from "framer-motion";
import AppLink from "@/components/UI/Links/AppLink/AppLink"
import Button, { ButtonKind, ButtonSize } from "@/components/UI/Buttons/Button/Button"
import { useSelf } from "@/apiUtilities/queries/useSelf"
import { BREAKPOINTS, FEATURES } from "@/utils/constants"
import { Tooltip } from "@mui/material"
import LinkButton, { LinkButtonKind, LinkButtonSize } from "../UI/Buttons/LinkButton/LinkButton"
import { useDynamicSignUpURL } from "@/apiUtilities/queries/useDynamicSignUpURL"

interface PlanProps {
	plan : Membership,
	selected: boolean,
	setSelected: () => void,
	presentational: boolean,
	withButtons: boolean,
	handleSubscribe?: (price: string, plan: Membership) => void,
	handleFreeTrial?: () => void,
	active: boolean,
	discount: any
}

export default function Plan({ plan, selected, setSelected, presentational = false, withButtons = false, handleSubscribe, handleFreeTrial, active = true, discount }: PlanProps) {
	const t = useTranslations("Memberships")
	const intervals = useTranslations("Memberships.intervals")
	const { width } = useWindowSize()
	const self = useSelf()
	const [selectedPrice, setSelectedPrice] = useState<string>(plan.prices[0].price_id)

	function handleSelectPrice(price:Price) {
		setSelectedPrice(price.price_id)
	}

	const currentMembership = 
		self && self.isSuccess && self.data.purchase && !self.data.purchase.expired && self.data.purchase.product_id 
			? self.data.purchase.product_id 
			: ''

	const { data: dynamicSignUpURLData } = useDynamicSignUpURL({
			enabled: FEATURES.signUpExternal && FEATURES.signUpExternalDynamic
		})
		const signUpLink = useMemo(() => {
		    if (!FEATURES.signUp) return '/login'
			if (!FEATURES.signUpExternal) return '/sign-up'
			if (!FEATURES.signUpExternalDynamic) {
			return process.env.NEXT_PUBLIC_FEATURE_SIGNUP_EXTERNAL_LINK ?? ''
			}
			return dynamicSignUpURLData?.url || ''
		}, [dynamicSignUpURLData])
	return (
		<motion.div className={`${styles.card} ${!active ? styles.unavailable : ''} ${currentMembership === plan.membership_id && !presentational ? styles.disabled : ''} ${presentational ? styles.presentational : ''}`} >
			{!selected && width <= BREAKPOINTS.LAPTOP &&
			<div className={`${styles.planBrief} ${presentational ? styles.presentational : ''}`} onClick={setSelected}>
				{presentational && plan.free_trial_eligible &&
				<div className={styles.ribbon}>
					<span className="bodyTextSmallStrong">{t('freeTrial24H')}</span>
				</div>
				}
				<span>{plan.title}</span>
				{plan.prices[0].nominal_price && (!plan.prices[0].buy_price || plan.prices[0].nominal_price.amount !== plan.prices[0].buy_price.amount) && <span className={`${styles.nominalPriceWrapper}`}>{plan.prices[0].nominal_price && <div className={`${styles.nominalPrice} bodyTextSmall`}>{Number(plan.prices[0].nominal_price.amount) / 100}{getSymbolFromCurrency(plan.prices[0].nominal_price.currency)}</div>}</span>}
				{plan.prices[0].buy_price && <span>{plan.prices[0].buy_price && <div className={`${plan.prices[0].nominal_price && plan.prices[0].buy_price && plan.prices[0].nominal_price.amount !== plan.prices[0].buy_price.amount ? styles.buyPrice : ''} largeButton`}>{Number(plan.prices[0].buy_price.amount) / 100}{getSymbolFromCurrency(plan.prices[0].buy_price.currency)}</div>}</span>}
			</div>
			}

			{(width > BREAKPOINTS.LAPTOP || selected) &&
			<div className={styles.plan}>
				{presentational && plan.free_trial_eligible &&
				<div className={styles.ribbon}>
					<span className="bodyTextSmallStrong">{t('freeTrial24H')}</span>
				</div>
				}
				<h3>{plan.title}</h3>
				<p>
					{plan.description}
				</p>
				{(!discount || (discount && discount.type === 'discount') || !active) && <div className={`${styles.prices} ${presentational ? styles.presentational : ''}`}>
					{plan.prices.map(price => {
						return (
							<div key={price.price_id} className={`${styles.price} ${price.price_id === selectedPrice ? styles.selected : ''}`} onClick={() => handleSelectPrice(price)}>
								<div className={styles.costs}>
								{price.nominal_price && (!price.buy_price || price.nominal_price.amount !== price.buy_price.amount) && <div className={`${styles.nominalPrice} bodyTextSmall`}>{Number(price.nominal_price.amount) / 100}{getSymbolFromCurrency(price.nominal_price.currency)}</div>}
								{price.buy_price && <div className={`${price.nominal_price && price.buy_price && price.nominal_price.amount !== price.buy_price.amount ? styles.buyPrice : ''} largeButton`}>{Number(price.buy_price.amount) / 100}{getSymbolFromCurrency(price.buy_price.currency)}</div>}
								</div>
								{intervals(price.interval)}
							</div>
						)
					})}
				</div>}
				{
					(discount && discount.type === 'promo' && active) && <div className={`${styles.prices} ${styles.promo} ${styles.selected}`}>
						<div className={styles.costs}>
							 <div className={`${styles.buyPrice} largeButton`}>{t('free')}</div>
						</div>
						{discount.duration === 1 && t('promoInterval')}
						{discount.duration > 1 && t('promoIntervals', { periods: discount.duration })}
					</div>
				}
				<div className={styles.features}>
					{plan.features.map(feature => {
						return (
							<div key={feature.description} className={styles.description}>
								<Icon icon={Icon_Check} className={styles.icon} />
								<p>{feature.description}</p>
								{feature.note && 
									<Tooltip 
										title={feature.note} 
										arrow 
										placement="right"
										slotProps={{
											popper: {
												modifiers: [
													{
														name: 'customStyle',
														enabled: true,
														phase: 'beforeWrite',
														fn: ({ state }) => {
															Object.assign(state.styles.popper, {
																maxWidth: '250px',
																width: 'max-content'
															});
														}
													}
												]
											},
											tooltip: {
												sx: { maxWidth: 250 }
											}
										}}
									>
										<Icon icon={Icon_Info} className={styles.icon} />
									</Tooltip>}
							</div>
						)
					})}
				</div>
				<AppLink href={`/catalogue?membership=${plan.membership_id}`} className={styles.link}>{t('gamesInThisMembership')}</AppLink>
				<div className={styles.buttons}>
				{!presentational && plan.free_trial_eligible && 
					<Button size={ButtonSize.MEDIUM} disabled={currentMembership === plan.membership_id || !active} kind={ButtonKind.SUGGESTED} onClick={handleFreeTrial && (() => handleFreeTrial()) } className={styles.button}>
						{t('freeTrial24H')}
					</Button>
				}
				{presentational && withButtons && plan.free_trial_eligible && 
					<LinkButton size={LinkButtonSize.MEDIUM} kind={LinkButtonKind.SUGGESTED} href={signUpLink} className={styles.button}>
						{t('freeTrial24H')}
					</LinkButton>
				}
				{!presentational && 
					<Button size={ButtonSize.MEDIUM} disabled={currentMembership === plan.membership_id || !active} onClick={handleSubscribe && (() => handleSubscribe(selectedPrice, plan)) } className={styles.button}>
						{t('subscribeNow')}
					</Button>
				}
				{presentational && withButtons &&
					<LinkButton size={LinkButtonSize.MEDIUM} href={signUpLink} className={styles.button}>
						{t('subscribeNow')}
					</LinkButton>
				}
				</div>
			</div>
			}
		</motion.div>
	)
}